<template>
  <main class="profit-subscription__main">
    <div
      v-if="props.profitOptions.howToWork"
      class="profit-subscription__main-section"
    >
      <Heading :level="3" class="profit-subscription__section-title">
        {{ props.profitOptions.howToWork.title }}
      </Heading>
      <BaseText size="p4">
        {{ props.profitOptions.howToWork.description }}
      </BaseText>
    </div>
    <div
      v-if="props.profitOptions.profits"
      class="profit-subscription__main-section"
    >
      <Heading :level="3" class="profit-subscription__section-title">
        {{ props.profitOptions.profits.title }}
      </Heading>
      <BaseText size="p4">
        {{ props.profitOptions.profits.description }}
      </BaseText>
      <div class="profit-subscription__button-box-container">
        <ButtonBoxDecide
          v-for="buttonBox in props.profitOptions.profits.options"
          :key="buttonBox.value"
          :icon="buttonBox.icon"
          :badge-label="buttonBox.observation"
          :secondary-text="buttonBox.description"
          :class="setButtonStyle(buttonBox.observation)"
          @click="goToProfitPreview(buttonBox.value)"
        >
          <template #label>
            <Heading :level="4">
              {{ buttonBox.title }}
            </Heading>
          </template>
        </ButtonBoxDecide>
      </div>
    </div>
    <div
      v-if="props.profitOptions.benefits"
      class="profit-subscription__main-section"
    >
      <Heading :level="3" class="profit-subscription__section-title">
        {{ props.profitOptions.benefits.title }}
      </Heading>
      <Row
        v-for="(benefit, index) in props.profitOptions.benefits.options"
        :key="benefit.title"
        :icon="benefit.icon"
        :left-title="benefit.title"
        :left-subtitle="benefit.description"
        :has-line="index !== 0"
      >
        <template #icon-left>
          <InitialAvatarIcon :icon="benefit.icon" size="small" />
        </template>
      </Row>
    </div>
    <div
      v-if="props.profitOptions.costs"
      class="profit-subscription__main-section"
    >
      <Heading :level="3" class="profit-subscription__section-title">
        {{ props.profitOptions.costs.title }}
      </Heading>
      <BaseText class="profit-subscription__section-text" size="p4">
        {{ props.profitOptions.costs.description }}
      </BaseText>
    </div>
    <div
      v-if="props.profitOptions.cancellation"
      class="profit-subscription__main-section"
    >
      <Heading :level="3" class="profit-subscription__section-title">
        {{ props.profitOptions.cancellation.title }}
      </Heading>
      <BaseText size="p4">
        {{ props.profitOptions.cancellation.description }}
      </BaseText>
      <div class="profit-subscription__section-buttons">
        <ButtonPrimary
          :label="props.profitOptions.cancellation.button.text"
          @click="goToProfitCancellation"
        />
        <ButtonSecondary
          :label="props.profitOptions.cancellation.footer"
          href="https://ajuda.nelogica.com.br/hc/pt-br"
          target="_blank"
        />
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import {
  ButtonBoxDecide,
  Heading,
  BaseText,
  Row,
  InitialAvatarIcon,
  ButtonPrimary,
  ButtonSecondary
} from '@warrenbrasil/nebraska-web';
import type { ProfitOptions } from '@/modules/trade/services/profit';
import { useRouter } from 'vue-router/composables';

interface Props {
  profitOptions: ProfitOptions;
}

const props = defineProps<Props>();
const router = useRouter();

function goToProfitPreview(profitValue: number) {
  const profitValueAsIdentifier = profitValue.toString();
  router.push({
    name: 'profitPreview',
    params: { profit: profitValueAsIdentifier }
  });
}

function goToProfitCancellation() {
  router.push({ name: 'profitCancellation' });
}

function setButtonStyle(observation: string) {
  const activeButton = /Ativo/.test(observation)
    ? 'profit-subscription__button-box--active'
    : '';
  return `profit-subscription__button-box ${activeButton}`;
}
</script>

<style lang="less" scoped>
.profit-subscription {
  &__main {
    width: 100%;
    max-width: 801px;
    margin: @size-spacing-x800 auto;

    .to-md({
      padding: 0 @size-spacing-x450;
    });
  }

  &__main-section {
    margin-bottom: @size-spacing-x800;
  }

  &__section-title {
    margin-bottom: @size-spacing-x400;
  }

  &__section-text {
    white-space: pre-line;
  }

  &__section-buttons {
    margin-top: @size-spacing-x400;
    display: flex;
    gap: @size-spacing-x400;
  }

  &__button-box-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: @size-spacing-x400;
    margin-top: @size-spacing-x800;

    .to-sm({
      grid-template-columns: 1fr;
    });
  }

  &__button-box {
    width: 392px;

    :deep(.badge-label) {
      color: @status-positive;
      background-color: @status-positive-background;
    }

    &--active {
      :deep(.badge-label) {
        color: @status-info;
        background-color: @status-info-background;
      }
    }

    .to-md({
      width: 100%;
    });

    .to-sm({
      inline-size: fit-content;
    });
  }
}
</style>
